import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { EmailInput } from '../forms/EmailInput';
import { SuperAdminService, CompanyAdminCreateData } from '../../services/superAdminService';
import { CompanyService } from '../../services/companyService';
import { Company } from '../../types';

interface CreateCompanyAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (adminData: { email: string; companyName: string }) => void;
}

interface FormData {
  companyId: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
}

interface ValidationErrors {
  companyId?: string;
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  phone?: string;
}

const CreateCompanyAdminModal: React.FC<CreateCompanyAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    companyId: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });

  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showEmailSentDialog, setShowEmailSentDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const superAdminService = React.useMemo(() => new SuperAdminService(), []);
  const companyService = React.useMemo(() => new CompanyService(), []);

  const resetForm = React.useCallback(() => {
    setFormData({
      companyId: '',
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
    });
    setErrors({});
    setSubmitError('');
    setShowConfirmation(false);
    setIsEmailValid(false);
  }, []);

  const loadCompanies = React.useCallback(async () => {
    setIsLoadingCompanies(true);
    try {
      const companiesData = await companyService.getAllCompanies();
      // アクティブな会社のみを表示
      setCompanies(
        companiesData.filter(
          company =>
            company.subscriptionStatus === 'active' || company.subscriptionStatus === 'trial'
        )
      );
    } catch (error) {
      logger.error('Failed to load companies:', error);
      setSubmitError('会社一覧の取得に失敗しました');
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [companyService]);

  // 会社一覧の取得
  useEffect(() => {
    if (isOpen) {
      loadCompanies();
      // モーダルを開くたびにフォームをリセット
      resetForm();
    }
  }, [isOpen, loadCompanies, resetForm]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // リアルタイムバリデーション
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // 確認用パスワードの一致チェック
    if (field === 'password' && formData.confirmPassword) {
      if (formData.confirmPassword !== value) {
        setErrors(prev => ({ ...prev, confirmPassword: 'パスワードが一致しません' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: undefined }));
      }
    }

    if (field === 'confirmPassword' && value !== formData.password) {
      setErrors(prev => ({ ...prev, confirmPassword: 'パスワードが一致しません' }));
    } else if (field === 'confirmPassword') {
      setErrors(prev => ({ ...prev, confirmPassword: undefined }));
    }
  };

  // メールアドレスバリデーション結果の処理
  const handleEmailValidationChange = (valid: boolean) => {
    setIsEmailValid(valid);
  };

  // メールアドレス変更ハンドラー
  const handleEmailChange = (_name: string, value: string) => {
    handleInputChange('email', value);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // 会社選択
    if (!formData.companyId) {
      newErrors.companyId = '会社を選択してください';
    }

    // 名前
    if (!formData.name.trim()) {
      newErrors.name = '名前を入力してください';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = '名前は2文字以上で入力してください';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = '名前は50文字以内で入力してください';
    }

    // メールアドレス（統一バリデーションを使用）
    if (!isEmailValid) {
      newErrors.email = 'メールアドレスを入力してください';
    }

    // パスワード
    if (!formData.password) {
      newErrors.password = 'パスワードを入力してください';
    } else if (formData.password.length < 8) {
      newErrors.password = 'パスワードは8文字以上で入力してください';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'パスワードは大文字・小文字・数字を含む必要があります';
    }

    // 確認用パスワード
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '確認用パスワードを入力してください';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致しません';
    }

    // 電話番号（任意）
    if (formData.phone && !/^[\d-+().\s]+$/.test(formData.phone)) {
      newErrors.phone = '正しい電話番号形式で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      const adminData: CompanyAdminCreateData = {
        email: formData.email,
        password: formData.password,
        name: formData.name.trim(),
        companyId: formData.companyId,
        phone: formData.phone.trim() || undefined,
      };

      await superAdminService.createCompanyAdmin(adminData);

      // メール送信通知ダイアログを表示
      setRegisteredEmail(formData.email);
      setShowEmailSentDialog(true);
    } catch (error: any) {
      logger.error('Failed to create company admin:', error);
      setSubmitError(error.message || '陸送会社管理者の作成に失敗しました');
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const getPasswordStrength = (
    password: string
  ): { score: number; text: string; color: string } => {
    if (!password) return { score: 0, text: '', color: 'text-gray-400' };

    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;

    const strength = [
      { text: '非常に弱い', color: 'text-red-600 dark:text-red-400' },
      { text: '弱い', color: 'text-red-500 dark:text-red-400' },
      { text: 'やや弱い', color: 'text-yellow-500 dark:text-yellow-400' },
      { text: '普通', color: 'text-yellow-400 dark:text-yellow-300' },
      { text: '強い', color: 'text-green-500 dark:text-green-400' },
      { text: '非常に強い', color: 'text-green-600 dark:text-green-400' },
    ];

    return { score, ...strength[score] };
  };

  const selectedCompany = companies.find(c => c.id === formData.companyId);
  const passwordStrength = getPasswordStrength(formData.password);

  const handleEmailDialogClose = () => {
    setShowEmailSentDialog(false);
    const company = companies.find(c => c.id === formData.companyId);
    onSuccess({
      email: registeredEmail,
      companyName: company?.name || '不明',
    });
    onClose();
    setRegisteredEmail('');
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='陸送会社管理者を作成' size='lg'>
      <div className='space-y-6'>
        {submitError && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4'>
            <div className='flex'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 dark:text-red-300' />
              <div className='ml-3'>
                <p className='text-sm text-red-800 dark:text-red-200'>{submitError}</p>
              </div>
            </div>
          </div>
        )}

        {showConfirmation && (
          <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4'>
            <div className='flex'>
              <CheckCircleIcon className='h-5 w-5 text-blue-400 dark:text-blue-300' />
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                  作成内容の確認
                </h3>
                <div className='mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1'>
                  <p>
                    <strong>会社:</strong> {selectedCompany?.name}
                  </p>
                  <p>
                    <strong>管理者名:</strong> {formData.name}
                  </p>
                  <p>
                    <strong>メールアドレス:</strong> {formData.email}
                  </p>
                  {formData.phone && (
                    <p>
                      <strong>電話番号:</strong> {formData.phone}
                    </p>
                  )}
                </div>
                <p className='mt-2 text-sm text-blue-600 dark:text-blue-300'>
                  この内容で陸送会社管理者を作成しますか？
                </p>
              </div>
            </div>
          </div>
        )}

        <div className='grid grid-cols-1 gap-6'>
          {/* 会社選択 */}
          <div>
            <label
              htmlFor='company'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              会社 <span className='text-red-500'>*</span>
            </label>
            <select
              id='company'
              value={formData.companyId}
              onChange={e => handleInputChange('companyId', e.target.value)}
              disabled={isLoadingCompanies}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.companyId
                  ? 'border-red-300 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value=''>
                {isLoadingCompanies ? '読み込み中...' : '会社を選択してください'}
              </option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name} ({company.code})
                </option>
              ))}
            </select>
            {errors.companyId && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.companyId}</p>
            )}
          </div>

          {/* 管理者名 */}
          <div>
            <label
              htmlFor='name'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              管理者名 <span className='text-red-500'>*</span>
            </label>
            <Input
              id='name'
              type='text'
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              placeholder='山田太郎'
              error={errors.name}
              maxLength={50}
            />
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              {formData.name.length}/50文字
            </p>
          </div>

          {/* メールアドレス */}
          <EmailInput
            name='email'
            label='メールアドレス'
            value={formData.email}
            onChange={handleEmailChange}
            onValidationChange={handleEmailValidationChange}
            placeholder='admin@example.com'
            required={true}
            disabled={isLoading}
            validationOptions={{ required: true }}
            error={errors.email}
            data-testid='admin-email-input'
          />

          {/* パスワード */}
          <div>
            <label
              htmlFor='password'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              パスワード <span className='text-red-500'>*</span>
            </label>
            <div className='relative'>
              <Input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={e => handleInputChange('password', e.target.value)}
                placeholder='8文字以上、大文字・小文字・数字を含む'
                error={errors.password}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute inset-y-0 right-0 pr-3 flex items-center'
              >
                {showPassword ? (
                  <EyeSlashIcon className='h-4 w-4 text-gray-400 dark:text-gray-300' />
                ) : (
                  <EyeIcon className='h-4 w-4 text-gray-400 dark:text-gray-300' />
                )}
              </button>
            </div>
            {formData.password && (
              <div className='mt-1 flex items-center space-x-2'>
                <div className='flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2'>
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score <= 2
                        ? 'bg-red-500'
                        : passwordStrength.score <= 3
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  />
                </div>
                <span className={`text-xs ${passwordStrength.color}`}>{passwordStrength.text}</span>
              </div>
            )}
          </div>

          {/* 確認用パスワード */}
          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              確認用パスワード <span className='text-red-500'>*</span>
            </label>
            <div className='relative'>
              <Input
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={e => handleInputChange('confirmPassword', e.target.value)}
                placeholder='パスワードを再入力してください'
                error={errors.confirmPassword}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute inset-y-0 right-0 pr-3 flex items-center'
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className='h-4 w-4 text-gray-400 dark:text-gray-300' />
                ) : (
                  <EyeIcon className='h-4 w-4 text-gray-400 dark:text-gray-300' />
                )}
              </button>
            </div>
          </div>

          {/* 電話番号（任意） */}
          <div>
            <label
              htmlFor='phone'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              電話番号 <span className='text-gray-500 dark:text-gray-400'>(任意)</span>
            </label>
            <Input
              id='phone'
              type='tel'
              value={formData.phone}
              onChange={e => handleInputChange('phone', e.target.value)}
              placeholder='03-1234-5678'
              error={errors.phone}
            />
          </div>
        </div>

        {/* ボタン */}
        <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700'>
          <Button
            variant='secondary'
            onClick={() => {
              if (showConfirmation) {
                setShowConfirmation(false);
              } else {
                onClose();
              }
            }}
            disabled={isLoading}
          >
            {showConfirmation ? '戻る' : 'キャンセル'}
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={isLoading || isLoadingCompanies}
            loading={isLoading}
          >
            {showConfirmation ? '管理者を作成' : '確認画面へ'}
          </Button>
        </div>
      </div>

      {/* メール送信通知ダイアログ */}
      {showEmailSentDialog && (
        <Modal
          isOpen={showEmailSentDialog}
          onClose={handleEmailDialogClose}
          title='ユーザー登録完了'
          size='md'
        >
          <div className='p-6'>
            <div className='flex items-start mb-4'>
              <CheckCircleIcon className='h-6 w-6 text-green-600 dark:text-green-400 mr-3 flex-shrink-0 mt-0.5' />
              <div>
                <p className='text-gray-900 dark:text-white font-medium mb-2'>
                  確認メールを送信しました
                </p>
                <p className='text-gray-700 dark:text-gray-300'>
                  <strong className='text-primary-600 dark:text-primary-400'>
                    {registeredEmail}
                  </strong>{' '}
                  宛に確認メールを送信しました。
                </p>
              </div>
            </div>
            <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6'>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                メール内のリンクをクリックして登録を完了してください。
                <br />
                メールが届かない場合は、迷惑メールフォルダもご確認ください。
              </p>
            </div>
            <div className='flex justify-end'>
              <Button onClick={handleEmailDialogClose} variant='primary'>
                OK
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default CreateCompanyAdminModal;
